import BigNumber from 'bignumber.js';
import { BigNumber as EthBigNumber } from 'ethers';
import { toChecksumAddress } from 'ethereum-checksum-address';
import { useQuery } from '@tanstack/react-query';

import { calculateApr } from '../../utils/calculateApr';
import { getMaxLeverage } from '../../utils/getMaxLeverage';
import { useRootStore } from '../../store/root';
import { POLLING_INTERVAL, QueryKeys } from '../../ui-config/queries';
import { IPool } from '../../modules/leverage/lists/ToLeveragePositionsList/types';
import { EPoolType, EPositionType, IEnrichedUniswapPosition } from '../../types/uniswapTokens';
import { findTokenInReserves } from '../../utils/findTokenInReserves';
import { useProtocolDataContext } from '../useProtocolDataContext';
import { useAppDataContext } from '../app-data-provider/useAppDataProvider';
import { useGlobalRevenueFeesQuery } from './useGlobalRevenueFeesQuery';
import { useAlmPoolsQuery } from './useAlmPoolQuery';

export interface IDisplayedPairsResponse {
  id: number;
  chain_id: number;
  token_0: string;
  token_1: string;
  pair_type: EPoolType;
  max_apr: string;
  max_apr_fee: number;
  url: string;
}

const INITIAL_DATA: IEnrichedUniswapPosition[] = [];

export const getDisplayedPairs = async ({ marketId, endpointURL }: { marketId?: number; endpointURL: string; }) => {
  try {
    const url = `${endpointURL}/concentrated_liquidity/displayed_pairs?market_id=${marketId}`;
    const result = await fetch(url);
    const json: IDisplayedPairsResponse[] = await result.json();
    return json;
  } catch (e) {
    return [];
  }
};

export const useDisplayedPairsQuery = () => {
  const { currentNetworkConfig } = useProtocolDataContext();
  const [chainId, account, isMarketsReady, marketData, getMarketDataById] = useRootStore((store) => [
    store.currentChainId,
    store.account,
    store.isMarketsReady,
    store.getCurrentChainMarketData(),
    store.getMarketDataById,
  ]);
  const endpointURL = currentNetworkConfig.yldrYpiUrl;
  const { reserves, loading: loadingReserves } = useAppDataContext();
  const { data: globalRevenueFees, isLoading: loadingGlobalRevenueFee } = useGlobalRevenueFeesQuery();
  const marketIds = marketData.map((item) => item.marketId);
  const { data: almPoolsData } = useAlmPoolsQuery({ chainId });

  const { data, ...rest } = useQuery({
    queryFn: () => {
      const requests = marketIds.map((marketId) => getDisplayedPairs({ marketId, endpointURL }));
      return Promise.all(requests);
    },
    queryKey: [QueryKeys.DISPLAYED_PAIRS, chainId, marketIds, account],
    enabled: Boolean(account) && isMarketsReady && !loadingReserves && !loadingGlobalRevenueFee,
    refetchInterval: POLLING_INTERVAL,
    select: (response) => response.reduce<IEnrichedUniswapPosition[]>((acc, marketResponse, index) => {
      marketResponse.filter((item) => {
        const token0 = findTokenInReserves({ reserves, address: item.token_0 });
        const token1 = findTokenInReserves({ reserves, address: item.token_1 });

        if (token0 && token1) {
          const poolApr = BigNumber(item.max_apr).div(100);

          const globalRevenueFee = globalRevenueFees.find((item) => {
            return item.marketId === marketIds[index];
          })?.revenueFee;
          const maxApr0 = calculateApr(poolApr, token0, globalRevenueFee);
          const maxApr1 = calculateApr(poolApr, token1, globalRevenueFee);
          const maxApr = BigNumber.max(poolApr, maxApr0, maxApr1);

          const availableLeverage0 = getMaxLeverage(token0);
          const availableLeverage1 = getMaxLeverage(token1);
          const maxLeverage = BigNumber.min(availableLeverage0, availableLeverage1);

          const almMarketName = almPoolsData.find((almPool) =>
            toChecksumAddress(almPool.token_0) === toChecksumAddress(token0.underlyingAsset) &&
            toChecksumAddress(almPool.token_1) === toChecksumAddress(token1.underlyingAsset) &&
            almPool.cl_market.id === marketIds[index]
          )?.market.name;

          acc.push({
            marketId: getMarketDataById(marketIds[index])?.marketId,
            marketName: getMarketDataById(marketIds[index])?.marketName,
            type: EPositionType.Pool,
            tokenId: item.id,
            token0: {
              symbol: token0.symbol,
              iconSymbol: token0.iconSymbol,
              underlyingAsset: toChecksumAddress(token0.underlyingAsset),
            },
            token1: {
              symbol: token1.symbol,
              iconSymbol: token1.iconSymbol,
              underlyingAsset: toChecksumAddress(token1.underlyingAsset),
            },
            liquidity: EthBigNumber.from(0),
            marketReferenceCurrencyValue: 0,
            maxLeverage: maxLeverage.toNumber(),
            uniswapLink: item.url,
            poolType: item.pair_type,
            fee: BigNumber(item.max_apr_fee).div(10000).toNumber(),
            maxApr: maxApr.toNumber(),
            almMarketName,
          } as IPool);
        }
      })
      return acc;
    }, []),
  });

  return {
    data: data || INITIAL_DATA,
    ...rest,
  }
}
